/*
 * Central list of Amplitude event names. Use these constants for all amplitudeLog calls
 * so event names stay in sync with shared/data/amplitude-events.csv.
 */
export const AMP_APP_LOGOUT = 'App - Logout';
export const AMP_APP_NAV_TO_DESIGN = 'App - Nav to Design';
export const AMP_APP_NAV_TO_ELEMENTS = 'App - Nav to Elements';
export const AMP_APP_NAV_TO_PRODUCTS = 'App - Nav to Products';
export const AMP_APP_NAV_TO_REPORT = 'App - Nav to Report';
export const AMP_APP_NODON_AI = 'App - Nodon AI';
export const AMP_APP_OPEN_EPD_SELECTOR = 'App - Open EPD Selector';
export const AMP_APP_OPEN_RECIPE_SELECTOR = 'App - Open Recipe Selector';
export const AMP_APP_ORGANIZATION_SELECT = 'App - Organization Select';
export const AMP_APP_PAGE_REFRESH_DUE_TO_NEW_APP_VERSION =
  'App - Page Refresh Due To New App Version';
export const AMP_APP_RELOAD = 'App - Reload';
export const AMP_BUILDING_VERSION_ADD = 'Building Version - Add';
export const AMP_BUILDING_VERSION_DELETE = 'Building Version - Delete';
export const AMP_BUILDING_VERSION_SELECT = 'Building Version - Select';
export const AMP_BUILDING_VERSION_UPDATE_NAME =
  'Building Version - Update Name';
/**
 * User promoted a proposal into a new baseline version (proposal kebab →
 * "New baseline from proposal"). Distinct from {@link AMP_BUILDING_VERSION_ADD}
 * (blank duplicate from the version menu).
 *
 * Payload:
 * - `VersionId` — id of the newly created baseline version
 * - `ProposalId` — id of the source proposal that was merged
 */
export const AMP_BASELINE_FROM_PROPOSAL = 'Baseline - Create From Proposal';
export const AMP_DESIGN_ACTIVITY_ADD = 'Design - Activity Add';
export const AMP_DESIGN_ACTIVITY_DELETE = 'Design - Activity Delete';
export const AMP_DESIGN_MAP_VIEW_MODE_TOGGLE = 'Design - Map View Mode Toggle';
export const AMP_DESIGN_MOVE_BUILDING_POINT = 'Design - Move Building Point';
export const AMP_DESIGN_PLACE_BUILDING = 'Design - Place Building';
export const AMP_DESIGN_RESULTS_UPDATE = 'Design - Results Update';
export const AMP_DESIGN_STOREY_ADD = 'Design - Storey Add';
export const AMP_DESIGN_STOREY_DELETE = 'Design - Storey Delete';
export const AMP_DESIGN_STOREY_MEZZANINE_TOGGLE =
  'Design - Storey Mezzanine Toggle';
export const AMP_DESIGN_MANUAL_GFA_UPDATE = 'Design - Manual GFA Update';
/**
 * User finished the PDF import wizard and placed one or more sheets on the map.
 * Payload: `PageCount`, `ProjectId`.
 */
export const AMP_DESIGN_PDF_UNDERLAY_IMPORT = 'Design - PDF Underlay Import';
/**
 * User removed one PDF sheet (or all sheets) from the Design map.
 * Payload: `UnderlayId` (omitted when every page is cleared), `ProjectId`.
 */
export const AMP_DESIGN_PDF_UNDERLAY_DELETE = 'Design - PDF Underlay Delete';
/**
 * User applied a known-length calibration on the map or in the import wizard.
 * Payload: `UnderlayId`, `Scope` (`active` | `all`), `ProjectId`.
 */
export const AMP_DESIGN_PDF_UNDERLAY_CALIBRATE =
  'Design - PDF Underlay Calibrate';
export const AMP_ELEMENT_ADD = 'Element - Add';
export const AMP_ELEMENT_APPLY_EPD = 'Element - Apply EPD';
export const AMP_ELEMENT_APPLY_RECIPE = 'Element - Apply Recipe';
export const AMP_ELEMENT_CATEGORY_SELECT = 'Element - Category Select';
export const AMP_ELEMENT_CREATE_ALTERNATIVE = 'Element - Create Alternative';
export const AMP_ELEMENT_DELETE = 'Element - Delete';
export const AMP_ELEMENT_DISABLE = 'Element - Disable';
export const AMP_ELEMENT_DUPLICATE = 'Element - Duplicate';
export const AMP_ELEMENT_EXPAND = 'Element - Expand';
export const AMP_ELEMENT_NAME_UPDATE = 'Element - Name Update';
export const AMP_ELEMENT_NEW_VERSION = 'Element - New Version';
export const AMP_ELEMENT_PROPERTY_ADD = 'Element - Property Add';
export const AMP_ELEMENT_PROPERTY_DELETE = 'Element - Property Delete';
export const AMP_ELEMENT_PROPERTY_UPDATE = 'Element - Property Update';
export const AMP_ELEMENT_QUANTITY_UPDATE = 'Element - Quantity Update';
export const AMP_ELEMENT_SELECT = 'Element - Select';
export const AMP_ELEMENT_UNIT_UPDATE = 'Element - Unit Update';
export const AMP_ELEMENT_USE_IN_PROPOSAL = 'Element - Use In Proposal';
export const AMP_ELEMENT_LIST_GROUP_BY_CODE = 'Element List - Group By Code';
export const AMP_ELEMENT_LIST_SEARCH = 'Element List - Search';
export const AMP_ELEMENT_LIST_SORT = 'Element List - Sort';
export const AMP_PAYWALL_CLOSE = 'Paywall - Close';
export const AMP_PAYWALL_OPEN = 'Paywall - Open';
export const AMP_PAYWALL_SUBMIT = 'Paywall - Submit';
export const AMP_PRODUCT_CREATE = 'Product - Create';
export const AMP_PRODUCT_DUPLICATE = 'Product - Duplicate';
export const AMP_PRODUCT_EDIT = 'Product - Edit';
export const AMP_PRODUCT_EPD_IMPORT = 'Product - EPD Import';
export const AMP_PRODUCT_UPDATE_A1_A3_GWP = 'Product - Update A1-A3 (GWP)';
export const AMP_PRODUCT_UPDATE_CARBON_SLIDER =
  'Product - Update Carbon Slider';
export const AMP_PRODUCT_UPDATE_COST = 'Product - Update Cost';
export const AMP_PRODUCT_UPDATE_LIFETIME = 'Product - Update Lifetime';
export const AMP_PRODUCT_UPDATE_REUSE = 'Product - Update Reuse';
export const AMP_PRODUCT_UPDATE_SPILL = 'Product - Update Spill';
export const AMP_PRODUCT_UPDATE_TRANSPORT = 'Product - Update Transport';
export const AMP_PRODUCT_UPDATE_UNIT = 'Product - Update Unit';
export const AMP_PRODUCT_LIST_APPLY_EPD_TO_MULTIPLE =
  'Product List - Apply EPD To Multiple Product Elements';
export const AMP_PRODUCT_LIST_GO_TO_ELEMENT = 'Product List - Go To Element';
export const AMP_PRODUCT_LIST_GROUP_BY_CATEGORY =
  'Product List - Group By Category';
export const AMP_PRODUCT_LIST_PER_KG_PRODUCT = 'Product List - Per Kg Product';
export const AMP_PRODUCT_LIST_SEARCH = 'Product List - Search';
export const AMP_PRODUCT_LIST_SELECT_ITEM = 'Product List - Select Item';
export const AMP_PRODUCT_LIST_SORT = 'Product List - Sort';
export const AMP_PROJECT_ARCHIVE = 'Project - Archive';
export const AMP_PROJECT_CREATE_FROM_BLANK = 'Project - Create From Blank';
export const AMP_PROJECT_CREATE_FROM_FILE = 'Project - Create From File';
export const AMP_PROJECT_CREATE_FROM_TEMPLATE =
  'Project - Create From Template';
export const AMP_PROJECT_CREATE_TEMPLATE = 'Project - Create Template';
export const AMP_PROJECT_DELETE = 'Project - Delete';
export const AMP_PROJECT_DUPLICATE = 'Project - Duplicate';
export const AMP_PROJECT_EDIT_PROJECT = 'Project - Edit Project';
export const AMP_PROJECT_EDIT_TEMPLATE = 'Project - Edit Template';
export const AMP_PROJECT_EXPORT = 'Project - Export';
export const AMP_PROJECT_EXPORT_SPREADSHEET = 'Project - Export Spreadsheet';
export const AMP_PROJECT_EXPORT_BOVERKET_XML = 'Project - Export Boverket XML';
export const AMP_PROJECT_FOLDER_DELETE = 'Project - Folder Delete';
export const AMP_PROJECT_FOLDER_RENAME = 'Project - Folder Rename';
export const AMP_PROJECT_LOCK = 'Project - Lock';
export const AMP_PROJECT_NEW_PROJECT_BUTTON_CLICKED =
  'Project - New Project Button Clicked';
export const AMP_PROJECT_OPEN_SETTINGS = 'Project - Open Settings';
export const AMP_PROJECT_SAVE_AS_TEMPLATE = 'Project - Save As Template';
export const AMP_PROJECT_SELECT = 'Project - Select';
export const AMP_PROJECT_SHARE = 'Project - Share';
export const AMP_PROJECT_UNARCHIVE = 'Project - Unarchive';
export const AMP_PROJECT_UNLOCK = 'Project - Unlock';
export const AMP_PROJECT_UPDATE_PRODUCTS = 'Project - Update Products';
export const AMP_PROJECT_VIEW_HEADER_LIFECYCLE_SELECT =
  'Project View Header - Lifecycle Select';
export const AMP_PROJECT_VIEW_HEADER_SBEF_GROUP_SELECT =
  'Project View Header - SBEF Group Select';
export const AMP_PROJECT_VIEW_HEADER_COST_VISIBILITY =
  'Project View Header - Cost Visibility';
export const AMP_PROJECT_VIEW_HEADER_CO2E_VISIBILITY =
  'Project View Header - CO2e Visibility';
export const AMP_PROJECT_VIEW_HEADER_DISPLAY_CURRENCY =
  'Project View Header - Display Currency';
export const AMP_PROJECT_VIEW_HEADER_SCOPE_PRESET_SELECT =
  'Project View Header - Scope Preset Select';
export const AMP_PROPOSAL_ADD = 'Proposal - Add';
export const AMP_PROPOSAL_CHANGE_NAME = 'Proposal - Change Name';
export const AMP_PROPOSAL_DELETE = 'Proposal - Delete';
export const AMP_PROPOSAL_DUPLICATE = 'Proposal - Duplicate';
export const AMP_PROPOSAL_DRAG_AND_DROP = 'Proposal - Drag And Drop';
export const AMP_PROPOSAL_SELECT = 'Proposal - Select';
export const AMP_RECIPE_CREATE = 'Recipe - Create';
export const AMP_RECIPE_DELETE = 'Recipe - Delete';
export const AMP_RECIPE_UPDATE_DESCRIPTION = 'Recipe - Update Description';
export const AMP_RECIPE_UPDATE_NAME = 'Recipe - Update Name';
export const AMP_RECIPE_MENU_ACTION_CLEAR = 'Recipe Menu Action - Clear';
export const AMP_RECIPE_MENU_ACTION_DETACH = 'Recipe Menu Action - Detach';
export const AMP_RECIPE_MENU_ACTION_RESET = 'Recipe Menu Action - Reset';
export const AMP_RECIPE_MENU_ACTION_SAVE = 'Recipe Menu Action - Save';
export const AMP_RECIPE_MENU_ACTION_SAVE_AS = 'Recipe Menu Action - Save As';
export const AMP_REPORT_ENTER_FULLSCREEN_MODE =
  'Report - Enter Fullscreen Mode';
export const AMP_REPORT_EXPORT_PDF = 'Report - Export PDF';
